import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { PRODUCTS } from "../data/products";
import { SITE } from "../data/site";
import { useLang } from "../i18n";
import { PRODUCT_I18N } from "../i18n-products";
import { MaskedLine, Reveal } from "../components/Reveal";
import { SmartImage } from "../components/SmartImage";
import { WhatsAppIcon } from "../components/FloatingContact";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const index = PRODUCTS.findIndex((p) => p.slug === slug);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} | Sana Steel Industries, Bengaluru`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", `${product.name} — ${product.tagline} Manufactured in Bengaluru by Sana Steel Industries.`);
    }
  }, [product]);

  if (!product) return <Navigate to="/products" replace />;

  return (
    <main data-testid="product-detail-page" className="pt-20">
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal>
            <Link to="/products" data-testid="product-back-link"
                  className="inline-flex items-center gap-2 font-heading text-xs font-bold uppercase tracking-[0.2em] text-steel hover:text-navy transition-colors duration-200">
              <ArrowLeft size={14} /> {t("detail.back")}
            </Link>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-6">
              <Reveal>
                <div className="relative">
                  <div className="absolute -top-4 -left-4 w-full h-full border-2 border-steel" />
                  <div className="relative overflow-hidden">
                    <SmartImage src={product.image} alt={product.name}
                                className="w-full h-[340px] md:h-[480px] object-cover" />
                    <span className="absolute top-0 left-0 bg-navy text-white font-heading text-sm font-bold px-4 py-3">
                      {String(index + 1).padStart(2, "0")} / 09
                    </span>
                  </div>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <MaskedLine delay={0.1}>
                <span className="section-eyebrow">{t(`tag.${product.slug}`, product.tagline)}</span>
              </MaskedLine>
              <h1 className="mt-4 font-heading font-black uppercase tracking-tighter text-navy leading-[0.98] text-4xl md:text-6xl">
                <MaskedLine delay={0.25}>{product.name}</MaskedLine>
              </h1>
              <Reveal delay={0.2}>
                <p className="mt-6 text-steel text-base md:text-lg leading-relaxed">
                  {PRODUCT_I18N[lang]?.[product.slug]?.desc || product.description}
                </p>
                <ul className="mt-8 space-y-3">
                  {(PRODUCT_I18N[lang]?.[product.slug]?.features || product.features).map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm md:text-base text-ink">
                      <Check size={18} className="mt-0.5 shrink-0 text-navy" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to={`/quote?product=${product.slug}`} data-testid="product-enquire-button" className="btn-primary">
                    {t("detail.enquire")} <ArrowRight size={16} />
                  </Link>
                  <a href={SITE.whatsapp} target="_blank" rel="noreferrer" data-testid="product-whatsapp-button"
                     className="inline-flex items-center gap-2 border border-gray-300 text-navy font-heading font-bold uppercase tracking-wide text-sm px-8 py-4 transition-colors duration-200 hover:border-[#25D366] hover:text-[#25D366]">
                    <WhatsAppIcon size={16} /> WhatsApp
                  </a>
                </div>
              </Reveal>
            </div>
          </div>

          {product.table && (
            <Reveal className="mt-20" data-testid="product-size-table">
              <h2 className="font-heading text-2xl md:text-3xl font-black uppercase tracking-tight text-navy">
                {product.table.title}
              </h2>
              <div className="mt-6 overflow-x-auto border border-gray-200">
                <table className="w-full text-sm md:text-base">
                  <thead>
                    <tr className="bg-navy text-white">
                      {product.table.columns.map((c) => (
                        <th key={c} className="text-left font-heading text-xs font-bold uppercase tracking-[0.15em] px-3 py-3 sm:px-6 sm:py-4">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {product.table.rows.map((r, ri) => (
                      <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-surface"}>
                        {r.map((cell, ci) => (
                          <td key={ci} className="px-3 py-3 sm:px-6 sm:py-4 border-b border-gray-200 text-ink">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-xs text-steel">
                {t("detail.customNote").replace("{phone}", SITE.phone1)}
              </p>
            </Reveal>
          )}
        </div>
      </section>
    </main>
  );
}
